"use client"

import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import { useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  extractProperty,
  extractPropertyMediaItems,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { ResponsiveMedia } from "./common/bit/MediaRenderer"
import PortContainer, { PortContainerProps } from "./PortContainer"

import "swiper/css"
import "swiper/css/effect-fade"
import { PauseIcon, Play2Icon } from "./icons"
import PortPrimaryButton from "./PortPrimaryButton"

const StyledSection = styled.section`
  display: flex;
  color: white;
  width: 100%;
  height: 487px;
  position: relative;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 753px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 1130px;
  }
`

const ControlButton = styled(IconButton)<{ $isPlaying: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease-out;
  color: white;
  & svg {
    color: inherit;
  }
  &:hover {
    background-color: ${({ theme }) => theme.colors.port.defaultTextColor};
    color: white;
  }
`
const StyledMedia = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  top: 0;
  left: 0;
  & > div {
    width: 100%;
    height: 100%;
  }
  &::after {
    content: "";
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    background-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) 24.57%,
      rgba(0, 0, 0, 0.8) 98.67%
    );
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledItemTitle = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 30px;
  font-weight: 400;
  line-height: 125%; /* 37.5px */
  letter-spacing: -0.6px;
  text-transform: capitalize;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    letter-spacing: -0.8px;
  }
`

const StyledSubtitle = styled.p`
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  text-shadow: 0 4px 23.1px #000;
  text-transform: capitalize;
  white-space: pre-line;
  margin-top: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const StyledContentSwiper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  z-index: 2;
  .swiper {
    padding-block: 0 !important;
  }
  .swiper-slide {
    opacity: 0 !important;
  }
  .swiper-slide-active {
    opacity: 1 !important;
  }
`
const StyledMediaSwiper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  z-index: 1;
  .swiper {
    padding-block: 0 !important;
  }
`

const StyledItem = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
`

const StyledPortContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 800px;
  width: 100%;
`

const StyledControlsWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 0;
  z-index: 10;
  display: flex;
  align-items: flex-end;
  gap: 12px;
`

const StyledPagination = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledPaginationBullet = styled.button<{ $isActive: boolean }>`
  width: 11px;
  height: 11px;
  padding: 0;
  background-color: transparent;
  border: 1px solid ${({ $isActive }) => ($isActive ? "white" : "rgba(255, 255, 255, 0.6)")};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${({ $isActive }) => ($isActive ? "white" : "transparent")};
    transition: background-color 0.3s ease;
  }

  &:hover {
    border-color: white;
  }
`

type ItemProps = {
  title?: string
  subtitle?: string
  button?: any
  desktopMedia?: string | undefined
  mobileMedia?: string | undefined
}

const MediaItem = ({ title, desktopMedia, mobileMedia }: ItemProps) => {
  const dMedia = getUmbracoMediaUrl(desktopMedia || "")
  const mMedia = getUmbracoMediaUrl(mobileMedia || "")
  const hasMedia = dMedia || mMedia
  const altText = title || "Port Image Carousel Item"

  return (
    <StyledItem>
      {hasMedia && (
        <StyledMedia>
          {
            <ResponsiveMedia
              desktopMedia={dMedia ? dMedia : undefined}
              mobileMedia={mMedia ? mMedia : undefined}
              alt={altText}
              priority
            />
          }
        </StyledMedia>
      )}
    </StyledItem>
  )
}

const PortContainerWrapper = ({ children, sx, maxWidth, maxWidthXL }: PortContainerProps) => {
  return (
    <PortContainer
      paddingInline={16}
      maxWidth={maxWidth || 1040}
      maxWidthXL={maxWidthXL || 1440}
      sx={{
        height: "100%",
        display: "flex",
        gap: "16px",
        flexDirection: {
          xs: "column",
          md: "row",
        },
        justifyContent: {
          md: "space-between",
        },
        alignItems: {
          md: "flex-end",
        },
        ...sx,
      }}
    >
      {children}
    </PortContainer>
  )
}

const ContentItem = ({ title, subtitle, button }: ItemProps) => {
  return (
    <PortContainerWrapper
      sx={{
        paddingBottom: {
          xs: "72px",
          md: "150px",
          xl: "210px",
        },
      }}
    >
      <StyledItem>
        <StyledPortContent>
          {title && <StyledItemTitle>{title}</StyledItemTitle>}
          {subtitle && (
            <StyledSubtitle
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(subtitle).replace(/\n/g, "<br>"),
              }}
            />
          )}

          <PortPrimaryButton
            sx={{
              maxWidth: {
                xs: "max-content",
              },
              height: {
                xs: "37px",
                md: "48px",
              },
              marginTop: {
                xs: "24px",
              },
            }}
            button={button}
          />
        </StyledPortContent>
      </StyledItem>
    </PortContainerWrapper>
  )
}

type PortImageCarouselProps = {
  data: any
}

const PortImageCarousel = ({ data }: PortImageCarouselProps) => {
  const [isPlaying, setIsPlaying] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const mediaSwiperRef = useRef<SwiperType | null>(null)
  const contentSwiperRef = useRef<SwiperType | null>(null)

  const itemsList = extractProperty(data, "itemsList")
  const items = itemsList?.map((item: any) => ({
    title: extractProperty(item, "title"),
    subtitle: extractProperty(item, "subtitle"),
    desktopMedia: extractPropertyMediaItems(item, "desktopMedia")?.[0],
    mobileMedia: extractPropertyMediaItems(item, "mobileMedia")?.[0],
    button: extractProperty(item, "button")?.[0],
  }))

  const handlePlaybackToggle = () => {
    if (isPlaying) {
      contentSwiperRef.current?.autoplay?.stop()
    } else {
      contentSwiperRef.current?.autoplay?.start()
    }
    setIsPlaying(!isPlaying)
  }

  const handleContentSlideChange = (swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex)
    if (mediaSwiperRef.current && mediaSwiperRef.current.activeIndex !== swiper.realIndex) {
      mediaSwiperRef.current.slideTo(swiper.realIndex)
    }
  }

  const handleBulletClick = (index: number) => {
    contentSwiperRef.current?.slideToLoop(index)
  }

  // in case data fields are empty, return null
  if (!items || items.length === 0) {
    return null
  }

  return items && items?.length < 1 ? null : (
    <StyledSection id="port-image-carousel">
      <StyledMediaSwiper>
        <Swiper
          slidesPerView={1}
          speed={2000}
          onSwiper={(swiper) => {
            mediaSwiperRef.current = swiper
          }}
        >
          {items?.map((item: any, index: number) => (
            <SwiperSlide key={`${item.title}-${index}`}>
              <MediaItem
                title={item.title}
                desktopMedia={item.desktopMedia}
                mobileMedia={item.mobileMedia}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </StyledMediaSwiper>
      <StyledContentSwiper>
        <Swiper
          effect="fade"
          modules={[EffectFade, Autoplay]}
          slidesPerView={1}
          speed={2000}
          loop={items?.length > 1}
          autoplay={
            items?.length > 1
              ? {
                  delay: 6000,
                  disableOnInteraction: false,
                }
              : false
          }
          onSwiper={(swiper) => {
            contentSwiperRef.current = swiper
          }}
          onSlideChange={handleContentSlideChange}
        >
          {items?.map((item: any, index: number) => (
            <SwiperSlide key={`fade-${item.title}-${index}`}>
              <ContentItem title={item.title} subtitle={item.subtitle} button={item.button} />
            </SwiperSlide>
          ))}
        </Swiper>
      </StyledContentSwiper>
      {items?.length > 1 && (
        <StyledControlsWrapper>
          <PortContainerWrapper
            sx={{
              paddingBottom: {
                xs: "16px",
                md: "40px",
                xl: "100px",
              },
              display: "flex",
              flexDirection: { xs: "row" },
              justifyContent: { xs: "space-between" },
              alignItems: { xs: "center" },
              height: {
                xs: "max-content",
              },
            }}
          >
            <StyledPagination>
              {items?.map((_: any, index: number) => (
                <StyledPaginationBullet
                  key={`bullet-${index}`}
                  $isActive={index === activeIndex}
                  onClick={() => handleBulletClick(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </StyledPagination>
            <ControlButton
              aria-label={isPlaying ? "Pause slider autoplay slider" : "Resume slider autoplay"}
              onClick={handlePlaybackToggle}
              disableRipple
              $isPlaying={isPlaying}
            >
              {isPlaying ? <PauseIcon /> : <Play2Icon />}
            </ControlButton>
          </PortContainerWrapper>
        </StyledControlsWrapper>
      )}
    </StyledSection>
  )
}
export default PortImageCarousel
